import { Checkbox } from "antd";
import { useMemo } from "react";

import { applicationList } from "./applicationList";

export interface ApplicationPermissionSelectorProps {
    value?: string[];
    onChange?: (value: string[]) => void;
    disabled?: boolean;
}

/** Vue applications.vue 使用的功能点树，保持接口提交值为权限编码。 */
export function ApplicationPermissionSelector({
    value = [],
    onChange,
    disabled = false
}: ApplicationPermissionSelectorProps) {
    const groups = useMemo(
        () =>
            (applicationList[0]?.children || []).map(group => ({
                label: group.label,
                options: group.authOptions || []
            })),
        []
    );
    const allCodes = useMemo(() => groups.flatMap(group => group.options.map(option => option.auth)), [groups]);
    const selected = new Set(value);

    // 以 Vue 权限树的固定顺序输出，避免 React 重渲染时改变接口数组顺序。
    const updateSelection = (next: Set<string>) => {
        onChange?.(allCodes.filter(code => next.has(code)));
    };
    const setGroupSelection = (codes: string[], checked: boolean) => {
        const next = new Set(selected);
        codes.forEach(code => (checked ? next.add(code) : next.delete(code)));
        updateSelection(next);
    };

    const selectedCount = allCodes.filter(code => selected.has(code)).length;
    const rootChecked = allCodes.length > 0 && selectedCount === allCodes.length;
    const rootIndeterminate = selectedCount > 0 && !rootChecked;

    return (
        <div aria-label="application-permission-selector" className="flex flex-col gap-3">
            <Checkbox
                checked={rootChecked}
                disabled={disabled}
                indeterminate={rootIndeterminate}
                onChange={event => setGroupSelection(allCodes, event.target.checked)}
            >
                全选
            </Checkbox>
            {groups.map(group => {
                const groupCodes = group.options.map(option => option.auth);
                const groupSelectedCount = groupCodes.filter(code => selected.has(code)).length;
                const groupChecked = groupCodes.length > 0 && groupSelectedCount === groupCodes.length;
                const groupIndeterminate = groupSelectedCount > 0 && !groupChecked;

                return (
                    <div key={group.label} className="rounded border border-slate-200 p-3">
                        <Checkbox
                            checked={groupChecked}
                            disabled={disabled}
                            indeterminate={groupIndeterminate}
                            onChange={event => setGroupSelection(groupCodes, event.target.checked)}
                        >
                            {group.label}
                        </Checkbox>
                        <div className="mt-2 flex flex-wrap gap-x-6 gap-y-2 pl-6">
                            {group.options.map((option, optionIndex) => (
                                <Checkbox
                                    key={option.auth}
                                    checked={selected.has(option.auth)}
                                    disabled={disabled}
                                    onChange={event => {
                                        const next = new Set(selected);
                                        if (event.target.checked) {
                                            next.add(option.auth);
                                        } else if (optionIndex === 0) {
                                            // 对齐旧 Vue selectAuth：取消同组首项时，同时清空该组其他功能点。
                                            groupCodes.forEach(code => next.delete(code));
                                        } else {
                                            next.delete(option.auth);
                                        }
                                        updateSelection(next);
                                    }}
                                >
                                    {option.label}
                                </Checkbox>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default ApplicationPermissionSelector;
